import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { scoreColor, VERDICT_LABEL } from "@/hooks/useDispatcherScore";

interface Props {
  score?: number | null;
  verdict?: keyof typeof VERDICT_LABEL | null;
  reasoning?: string | null;
  className?: string;
  size?: "sm" | "md" | "lg";
  onClick?: (e: React.MouseEvent) => void;
  showEmpty?: boolean; // when true, renders a dash placeholder instead of null
}

export function ScoreBadge({ score, verdict, reasoning, className, size = "sm", onClick, showEmpty = false }: Props) {
  if (score == null) {
    if (!showEmpty) return null;
    return <span className={cn("text-muted-foreground/40 text-xs", className)}>—</span>;
  }
  const dims =
    size === "lg" ? "h-12 w-12 text-xl" : size === "md" ? "h-9 w-9 text-base" : "h-7 w-7 text-xs";

  const badge = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center justify-center rounded-full font-bold border-2 transition-transform hover:scale-110",
        dims,
        score >= 80
          ? "bg-brand-teal/10 border-brand-teal/40 text-brand-teal"
          : score >= 60
          ? "bg-amber-500/10 border-amber-500/40 text-amber-600"
          : "bg-muted border-border text-muted-foreground",
        onClick ? "cursor-pointer" : "cursor-default",
        className
      )}
    >
      {score}
    </button>
  );

  const tipText = verdict
    ? `${VERDICT_LABEL[verdict]}${reasoning ? ` — ${reasoning.slice(0, 110)}${reasoning.length > 110 ? "…" : ""}` : ""}`
    : `ATLAS score ${score}/100`;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">{tipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export { scoreColor };
